/**
 * The per-attack **target-legality gates** the room runs before any dice: reach and Threat for
 * Overwatch, range and line-of-sight for a unit or a bare cell, whether the named weapon can
 * fire at all, tech range, activate-site range — and Ram's own SIZE rule, which sits here beside
 * them because it is Ram's one gameplay-tier reject against a chosen target (adjacency itself is
 * gated by the shared reach check above).
 */

import type { Cell, EncounterState } from "@lancer-vtt/domain";
import { formatWeaponCandidate, selectWeaponProfile, superheavyBarsSkirmish, isItemDestroyed, latchedWeaponMode, primaryReach, terrainVisibility, threatValue, weaponInstanceKey, worldFor } from "@lancer-vtt/domain";
import type { WeaponMode } from "@lancer-vtt/domain";
import type { AttackTarget } from "@lancer-vtt/shared";
import { ok, err } from "@lancer-vtt/shared";
import { combatantCellRange, sightCut, combatantRange, footprintCells } from "../effects/spatialAnswerer";
import { attackTargetDistance, attackWeaponMode, isMeleeActionAttack, selectWeapon } from "../resolve";
import { checkLineOfSight } from "../search";
import type { AttackStance } from "../effects/seed";
import type { BudgetVerdict } from "./types";
import { noAttackRefusal } from "./weaponState";

/**
 * The fallback **threat range** for the (structural, never expected in play) case
 * `checkOverwatchRange` can't even select a weapon — no attacker on the map at all.
 * Every REAL Overwatch check derives its threat from the reacting weapon's own
 * `threatValue`; this constant is a dead-attacker carve-out, not a general default (RAW's own
 * weapon-Threat-defaults-to-1 lives in {@link import("@lancer-vtt/domain").threatValue}).
 */
export const DEFAULT_THREAT_RANGE = 1;
/**
 * May `attacker` make an **Overwatch** reaction-attack against `target`? The target must be
 * within the attacker's **weapon's THREAT value** — footprint-to-footprint min
 * `@lancer-vtt/space` distance vs
 * {@link import("@lancer-vtt/domain").threatValue} of the SAME frozen weapon profile
 * {@link selectWeapon} would fire (named `weapon`/`profile` off the reaction intent, else
 * the attacker's first frozen weapon / fallback — the identical selection `checkAttackRange`
 * uses). THREAT defaults to **1** (melee reach) for a weapon naming no explicit Threat band
 * (a plain Rifle threatens 1, not its 10-space firing range) — RAW's own default; an explicit
 * reach weapon (a Threat-2 polearm) uses its own band. Returns a verdict the
 * room surfaces. When either combatant is unplaced (no map position, `range === null`) the
 * threat range can't be measured ⇒ rejected (an Overwatch needs a placed target in reach).
 */
export type CheckOverwatchRangeArgs = {
  state: EncounterState;
  attacker: string;
  target: string;
  /** The named weapon id off the reaction intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkOverwatchRange(args: CheckOverwatchRangeArgs): BudgetVerdict {
  const { state, attacker, target, weapon, profile } = args;
  const atk = state.combatants[attacker];
  const w = atk ? selectWeapon(atk, weapon, profile) : undefined;
  // Ordnance (RAW Core p.105: "cannot be used for OVERWATCH") — a flat prohibition, checked
  // BEFORE range: an Ordnance weapon is never
  // offered/legal for Overwatch, independent of threat range or the turn-window predicate
  // ({@link checkOrdnanceWindow}) a standard/Barrage shot is gated by instead.
  if (w?.ordnance) {
    return err(`overwatch: ${w.name} is Ordnance — an Ordnance weapon can never be used for Overwatch (RAW p.105)`);
  }
  // No attacker on the map at all ⇒ the shared "not placed" reject below fires anyway
  // (combatantRange returns null), so the DEFAULT_THREAT_RANGE fallback here is inert —
  // it just avoids selecting a weapon off an undefined combatant.
  const threat = w ? threatValue(w.range ?? []) : DEFAULT_THREAT_RANGE;
  const range = combatantRange(state, attacker, target);
  if (range === null) {
    return err(`overwatch: ${attacker} or ${target} is not placed on the map (can't measure threat range)`);
  }
  if (range > threat) {
    return err(`overwatch: ${target} is ${range} away — outside ${attacker}'s threat range (${threat})`);
  }
  return ok(undefined);
}

/**
 * May `attacker` make a **standard weapon attack** against the unit `target`? The target must be
 * within the weapon's **max reach** — {@link primaryReach} over the
 * weapon's frozen `range` — measured against the footprint-to-footprint {@link combatantRange}.
 * The weapon is picked with the SAME `selectWeapon` the resolver uses (named weapon → first frozen
 * → fallback), so the gate measures exactly the weapon that would fire; range bands collapse to the
 * max Range/Threat reach via `primaryReach`. When either combatant is unplaced (`range === null`)
 * the distance can't be measured ⇒ rejected (a weapon attack needs a placed target in reach).
 *
 * **A weapon with no usable range data (`reach <= 0`) is NOT gated** — that is the resolver's
 * fallback 1d6 strike (a weaponless Everest) and any weapon carrying no `range` entries: range is
 * *unknown*, not *zero*, so the wall stands down rather than rejecting every shot (it mirrors
 * `attackReachCells`, which paints no wash when `reach <= 0`). The wall only constrains weapons that
 * actually declare a reach.
 *
 * **Thrown{N} extends reach past a melee weapon's Threat band** (RAW: "can be thrown at targets
 * within {VAL} spaces"). `tg_thrown`'s frozen `val` is read straight
 * off the weapon's frozen `tags` — the same frozen-profile-tag idiom `resolve/attackModifiers.ts`'s `isSeeking`
 * check reads (`weaponProfile.ts`'s `freezeTagRefs` keeps every raw tag ref, not just the ones the
 * profile also flattens into named fields) — and folded in as an ADDITIONAL legal reach alongside
 * the weapon's own Threat range, never a replacement: a Threat-1 knife carrying Thrown{3} is legal
 * at both adjacent (melee) AND range ≤ 3 (thrown), so `max(primaryReach, tg_thrown val)` is the
 * whole rule.
 *
 * **Distance only** — line-of-sight / cover are out of scope (the resolver still folds cover into
 * to-hit). This is the non-reaction, non-tech, unit-target gate; Overwatch reactions use
 * {@link checkOverwatchRange}.
 */
export type CheckAttackRangeArgs = {
  state: EncounterState;
  attacker: string;
  target: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
  /** Range added to a ranged weapon's reach (`AttackStance.rangeBonus`, Siege Stabilizers). */
  rangeBonus?: number;
};

/** Is `weapon`'s reach a Range (a ranged weapon), not only a Threat? */
function isRangedProfile(weapon: { range?: readonly { kind: string }[] }): boolean {
  return (weapon.range ?? []).some((r) => r.kind === "range");
}

export function checkAttackRange(args: CheckAttackRangeArgs): BudgetVerdict {
  const { state, attacker, target, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return err(`attack: ${attacker} is not on the map`);
  const selected = selectWeapon(atk, weapon, profile);
  const thrownReach = selected.tags?.find((t) => t.id === "tg_thrown")?.val ?? 0;
  const bonus = isRangedProfile(selected) ? args.rangeBonus ?? 0 : 0;
  const reach = Math.max(primaryReach(selected.range ?? []) + bonus, thrownReach);
  // No usable range data ⇒ range unknown, not zero — stand the wall down (the fallback strike).
  if (reach <= 0) return ok(undefined);
  const range = combatantRange(state, attacker, target);
  if (range === null) {
    return err(`attack: ${attacker} or ${target} is not placed on the map (can't measure range)`);
  }
  if (range > reach) {
    return err(`attack: ${target} is ${range} away — outside ${attacker}'s weapon reach (${reach})`);
  }
  return ok(undefined);
}

/**
 * May `attacker` make a **standard weapon attack** against the unit `target` given **line of
 * sight**? RAW (Core p.64) gates BOTH ranged and melee attacks on "within [Range/Threat] **and
 * line of sight**" — this is the sibling wall to {@link checkAttackRange} (distance). Delegates
 * to the SAME geometry the per-cell advisory and the cover fold already read
 * ({@link checkLineOfSight}, `search.ts`'s exported `terrainVisibility`-based helper) — never a
 * third LoS implementation.
 *
 * The weapon is picked with the SAME `selectWeapon` {@link checkAttackRange} uses (named weapon →
 * first frozen → fallback), so the gate measures exactly the weapon that would fire.
 *
 * **Two RAW exemptions (Core p.65), checked BEFORE the geometry call:** a weapon carrying
 * `tg_seeking` ("ignores cover and doesn't require line of sight") or `tg_arcing` ("can attack
 * targets without line of sight") stands the wall down entirely — same frozen-tag idiom the
 * attack spine's S2.5 cover fold reads ({@link import("../resolve/attackModifiers").foldAttackCover}'s
 * own `isSeeking`). Arcing is LoS-exempt but still folds cover there (an independent axis,
 * untouched by this gate) — a full `visible: false` block is
 * treated as fully exempt for Arcing (a deliberate simplification).
 *
 * **Mirrors `checkAttackRange`'s reach-unknown / off-map carve-outs:** a weapon with no usable
 * range data (`reach <= 0` — the resolver's fallback 1d6 strike) stands the wall down (range is
 * *unknown*, not measurable, so LoS can't be meaningfully gated either); an unplaced attacker or
 * target is an error (LoS can't be measured), surfaced by {@link checkLineOfSight} itself.
 *
 * **Melee is gated too** — `terrainVisibility` already folds edge-walls (`blocksEdge`), so a
 * melee swing across a wall-edge between two adjacent cells rejects for free, no special case.
 */
export type CheckAttackLoSArgs = {
  state: EncounterState;
  attacker: string;
  target: string;
  /** The named weapon id off the intent (else the attacker's first frozen weapon / fallback). */
  weapon?: string;
  /** The firing-profile selection off the intent (a multi-profile weapon). */
  profile?: string;
};

export function checkAttackLoS(args: CheckAttackLoSArgs): BudgetVerdict {
  const { state, attacker, target, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return err(`attack: ${attacker} is not on the map`);
  const selected = selectWeapon(atk, weapon, profile);
  const isSeeking = selected.tags?.some((t) => t.id === "tg_seeking") ?? false;
  const isArcing = selected.tags?.some((t) => t.id === "tg_arcing") ?? false;
  if (isSeeking || isArcing) return ok(undefined);
  const thrownReach = selected.tags?.find((t) => t.id === "tg_thrown")?.val ?? 0;
  const reach = Math.max(primaryReach(selected.range ?? []), thrownReach);
  // No usable range data ⇒ range unknown, not zero — stand the wall down, mirroring
  // `checkAttackRange`'s identical fallback-strike carve-out.
  if (reach <= 0) return ok(undefined);
  return checkLineOfSight({ state, viewer: attacker, target, verb: "attack" });
}

/**
 * May a standard **attack** (`request_attack`, non-tech — a standard shot or an Overwatch
 * reaction) name `weapon` off `attacker`'s frozen loadout — the attack-side sibling of
 * {@link checkBarrageOpener}'s weapon-on-loadout wall? Barrage always names a weapon; a plain
 * attack may **omit** it (`selectWeapon`'s auto-select/fallback path), so this wall's
 * omitted-weapon branch is what pins the surviving split of that fallback:
 *
 *   - A **named** weapon must exist on the frozen loadout (by instance key) — unless the mech
 *     is weaponless (`weapons.length === 0`, the same "weaponless still resolves" carve-out
 *     `checkBarrageOpener` keeps) or the name is a built-in melee-action id (Ram/Grapple/
 *     Improvised Attack — a synthetic profile, never a loadout entry, {@link isMeleeActionAttack}).
 *     An unknown key is a **hard reject, before any dice** — mirroring `checkBarrageOpener`'s
 *     "no weapon … on its loadout" message.
 *   - A **named** weapon that resolves but is **destroyed** is ALSO a **hard reject, before any
 *     dice** — an explicit ref to a destroyed weapon must not silently fall through to
 *     `selectWeapon`'s live-sibling/`FALLBACK_WEAPON` swap. **Scoped to the explicit-ref branch
 *     only** — the omitted-weapon auto-select/fallback path below keeps silently skipping
 *     destroyed weapons (`selectWeapon` already excludes them).
 *   - An **omitted** weapon is fine for **0 or 1** loadout weapons (the weaponless-strike /
 *     sole-weapon-auto-select cases `selectWeapon`'s `weapons[0]` fallback still serves) but a
 *     **hard reject for 2+** — omitting `--weapon` on a multi-weapon mech never means "whichever
 *     is first". The reject carries the SAME candidate list ({@link formatWeaponCandidate}) the
 *     CLI's ambiguous-ref reject uses, so it doubles as key-discovery at the moment of need, and
 *     makes `selectWeapon`'s silent `weapons[0]` branch unreachable for the multi-weapon case at
 *     this layer too.
 *
 * Never called for a tech attack (`intent.tech` selects no real loadout weapon — INVADE's
 * `act_invade` isn't a loadout entry) — the room scopes this the same way it scopes
 * {@link checkWeaponRecharge}/{@link checkLoaded}/{@link checkLimited}. `gm_force` is a
 * SEPARATE intent that skips this whole gate (never a flag), exactly like it already skips
 * Recharge/Loaded/Limited/Ordnance.
 */
export type CheckAttackWeaponArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent — undefined means "let `selectWeapon` auto-select". */
  weapon?: string;
};

export function checkAttackWeapon(args: CheckAttackWeaponArgs): BudgetVerdict {
  const { state, attacker, weapon } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined); // a missing attacker is caught by the range gate alongside this.
  if (isMeleeActionAttack(weapon)) return ok(undefined); // a synthetic action id, not a loadout ref.
  const weapons = atk.base.weapons ?? [];
  if (weapon !== undefined) {
    if (weapons.length > 0 && !weapons.some((w) => weaponInstanceKey(w) === weapon)) {
      return err(`attack: ${atk.name} has no weapon "${weapon}" on its loadout — name one by instance key: ${weapons.map(formatWeaponCandidate).join(", ")}`);
    }
    const named = weapons.find((w) => weaponInstanceKey(w) === weapon);
    if (named && isItemDestroyed(atk.current.resources, weaponInstanceKey(named))) {
      return err(`attack: weapon "${named.name}" (${weaponInstanceKey(named)}) is destroyed and cannot fire`);
    }
    return named?.noAttack === true ? err(noAttackRefusal(named)) : ok(undefined);
  }
  if (weapons.length >= 2) {
    return err(
      `attack: ${atk.name} carries ${weapons.length} weapons — name one with --weapon: ${weapons.map(formatWeaponCandidate).join(", ")}`,
    );
  }
  return weapons[0]?.noAttack === true ? err(noAttackRefusal(weapons[0])) : ok(undefined);
}

/**
 * May this Skirmish (or Overwatch, which RAW spells "SKIRMISH against that character as a
 * reaction") fire the named weapon? A **Superheavy** weapon can't: Core p.70 "SUPERHEAVY weapons
 * are too cumbersome to use in a SKIRMISH, and can only be fired as part of a BARRAGE" (p.71 again
 * under Barrage; the NPC Barrage on p.280 is the one NPC action naming "one Superheavy weapon").
 * The size is the weapon's frozen `mount` — a mech weapon's compendium size, an NPC weapon's
 * `weapon_type` lead word. The one exception is a profile frozen with lancer-data's `skirmish: true`
 * (the Leviathan's Standard profile, Core p.134; {@link superheavyBarsSkirmish}). A Barrage never reaches this gate; neither does a lock continuation or
 * Executioner's Backswing Cut free follow-up (which RAW grants for a Superheavy melee weapon).
 */
export type CheckSuperheavySkirmishArgs = {
  state: EncounterState;
  attacker: string;
  /** The named weapon id off the intent — undefined auto-selects, as {@link selectWeapon} does. */
  weapon?: string;
  /** The named firing profile off the intent — the Leviathan's Spin-Up can't Skirmish, its Standard can. */
  profile?: string;
  /** The verb for the refusal: `"skirmish"`, or `"overwatch"` for the reaction. */
  verb: "skirmish" | "overwatch";
};

export function checkSuperheavySkirmish(args: CheckSuperheavySkirmishArgs): BudgetVerdict {
  const { state, attacker, weapon, profile, verb } = args;
  const atk = state.combatants[attacker];
  if (!atk || isMeleeActionAttack(weapon)) return ok(undefined);
  const selected = selectWeapon(atk, weapon, undefined);
  if (!superheavyBarsSkirmish(selectWeaponProfile(selected, profile), selected.mount)) return ok(undefined);
  return err(`${verb}: ${selected.name} is a Superheavy weapon — it can only be fired as part of a Barrage (Core p.70)`);
}

/**
 * May this attack use an either-ranged-or-melee weapon (`WeaponProfile.eitherMode`, the Ram
 * Cannon) the way it resolves? Three rejects, all before any dice:
 *
 *   - `mode` named for a weapon that has no second mode — the flag would otherwise be silently
 *     ignored.
 *   - `mode: "melee"` against a target beyond the weapon's Threat. The range wall measures the
 *     weapon's max reach (Range 5), which a melee swing does not have.
 *   - the other mode than the one this weapon already used THIS turn (Core p.297: "either a
 *     ranged or melee weapon, but not both in the same turn"). The latch is keyed on the global
 *     turn ordinal ({@link latchedWeaponMode}), so an Elite's second activation and an off-turn
 *     Overwatch each start clean.
 *
 * A `gm_force` shot never reaches this gate (it skips the whole legality half), but its resolution
 * still stamps the latch, so the GM override counts toward the turn's mode.
 */
export type CheckWeaponModeArgs = {
  state: EncounterState;
  attacker: string;
  intent: { target: AttackTarget; weapon?: string; profile?: string; mode?: WeaponMode };
};

export function checkWeaponMode(args: CheckWeaponModeArgs): BudgetVerdict {
  const { state, attacker, intent } = args;
  const atk = state.combatants[attacker];
  if (!atk) return ok(undefined);
  const selected = selectWeapon(atk, intent.weapon, intent.profile);
  if (selected.eitherMode !== true) {
    if (intent.mode === undefined) return ok(undefined);
    return err(`attack: ${selected.name} is not a ranged-or-melee weapon — mode "${intent.mode}" applies only to a weapon that can be either`);
  }
  const mode = attackWeaponMode({ state, attacker: atk, intent, weapon: selected }) ?? "ranged";
  if (mode === "melee") {
    const threat = threatValue(selected.range ?? []);
    const distance = attackTargetDistance({ state, attacker: atk, intent });
    if (distance !== null && distance > threat) {
      return err(`attack: ${selected.name} used as a melee weapon reaches Threat ${threat}, and the target is ${distance} away — use it as a ranged weapon`);
    }
  }
  const latched = latchedWeaponMode({ resources: atk.current.resources, instanceKey: weaponInstanceKey(selected), turnOrdinal: state.activation.turnOrdinal });
  if (latched !== undefined && latched !== mode) {
    return err(`attack: ${atk.name} already used ${selected.name} as a ${latched} weapon this turn — it is ranged or melee, not both in the same turn (Core p.297)`);
  }
  return ok(undefined);
}

/**
 * May `attacker` make this attack under its {@link AttackStance} (Siege Stabilizers)? A melee
 * attack — a weapon whose reach is only a Threat, Ram, Grapple, an Improvised Attack — is refused
 * while it holds `cannotMelee`; a ranged one is refused at a unit or cell `minimumRange` or closer.
 */
export function checkAttackStance(args: { state: EncounterState; attacker: string; weapon?: string; profile?: string; target: AttackTarget; stance: AttackStance }): BudgetVerdict {
  const { state, attacker, weapon, profile, target, stance } = args;
  if (stance.cannotMelee === undefined && stance.minimumRange === undefined) return ok(undefined);
  const atk = state.combatants[attacker];
  if (!atk) return err(`attack: ${attacker} is not on the map`);
  const ranged = isRangedProfile(selectWeapon(atk, weapon, profile));
  if (!ranged && stance.cannotMelee !== undefined) return err(`attack: ${atk.name} can't make melee attacks (${stance.cannotMelee})`);
  if (ranged && stance.minimumRange !== undefined) {
    const range = target.kind === "unit" ? combatantRange(state, attacker, target.id) : combatantCellRange(state, attacker, target.cell);
    if (range !== null && range <= stance.minimumRange) return err(`attack: ${atk.name} can't make ranged attacks within range ${stance.minimumRange} (target ${range} away)`);
  }
  return ok(undefined);
}

/**
 * May `attacker` **RAM** the unit `target`? RAW (Core p.70): Ram targets "an adjacent character
 * **the same SIZE or smaller** than you" — so the target's Size must be **≤** the attacker's.
 * Rejected when `target.base.size > attacker.base.size`. This is Ram's **one** gameplay-tier
 * reject; **adjacency** is gated separately by {@link checkAttackRange} (Ram resolves through a
 * synthetic threat-1 weapon, so the shared reach gate already enforces "adjacent"). A hard reject
 * in the rules-following tier — there is no `force` flag; a GM who must Ram a larger unit reaches
 * for the separate force-apply attack, not a softened Ram.
 */
export function checkRamSize(args: { state: EncounterState; attacker: string; target: string; stance?: AttackStance }): BudgetVerdict {
  const { state, attacker, target, stance } = args;
  const atk = state.combatants[attacker];
  const tgt = state.combatants[target];
  if (!atk) return err(`ram: ${attacker} is not on the map`);
  if (!tgt) return err(`ram: ${target} is not on the map`);
  // Synthetic Muscle Netting: "You may Ram targets larger than you".
  if (tgt.base.size > atk.base.size && stance?.ramsLarger === undefined) {
    return err(
      `ram: ${tgt.name} (Size ${tgt.base.size}) is larger than ${atk.name} (Size ${atk.base.size}) — ` +
        `Ram targets a character the same Size or smaller`,
    );
  }
  return ok(undefined);
}

/**
 * May `actor` activate a granted action at the bare **cell** `site`? The activate-path sibling of
 * {@link checkAttackCellRange}, and the wall RAW's thrown deployables need: "Throw a Smoke
 * Grenade **within Range 5**" / "Throw a Frag Grenade within Range 5" carries no other
 * enforcement, so without this a charge could be thrown to any cell on the map (`resolveActivate`
 * accepts the site as recorded and gates no range itself).
 *
 * Reads the reach off {@link CombatantBase.actionRanges}, frozen at spawn from the action's own RAW
 * `range` — the resolve path is a pure reducer with no compendium handle, so the number has to be on
 * the combatant. Measured from the actor's footprint to the cell via {@link combatantCellRange}, the
 * identical geometry the attack cell gate uses (never a second distance implementation).
 *
 * Line of sight to the cell is checked after the distance, with `checkAttackCellLoS`'s geometry.
 *
 * **Ungated cases return `ok`, deliberately** — an action with no frozen reach (`actionRanges` has no
 * key: a self/adjacent action, a `deployables[]` deploy affordance, a pilot-effect row) and a reach
 * that parses `<= 0` both stand the wall down, matching the reach-unknown carve-out the attack gates
 * already make. An unplaced actor can't be measured and is rejected.
 */
export type CheckActivateSiteRangeArgs = {
  state: EncounterState;
  actor: string;
  action: string;
  site: Cell;
};

export function checkActivateSiteRange(args: CheckActivateSiteRangeArgs): BudgetVerdict {
  const { state, actor, action, site } = args;
  const act = state.combatants[actor];
  if (!act) return err(`activate: ${actor} is not on the map`);
  const frozen = act.base.actionRanges?.[action];
  if (!frozen) return ok(undefined); // no reach declared for this action — ungated, as `attackRange === null` is
  const reach = primaryReach(frozen);
  if (reach <= 0) return ok(undefined);
  const range = combatantCellRange(state, actor, site);
  if (range === null) {
    return err(`activate: ${actor} is not placed on the map (can't measure range)`);
  }
  if (range > reach) {
    return err(`${action}: target cell is ${range} away — outside ${act.name}'s reach (Range ${reach})`);
  }
  // RAW Core p.64: an area with a Range draws its origin "from a point within the range specified
  // and line of sight"; drones deploy "within SENSORS and line of sight". The geometry
  // `checkAttackCellLoS` uses.
  const world = worldFor(state);
  const cut = sightCut(state, act, [site]);
  if (cut !== undefined) return err(`${action}: ${cut}`);
  const view = terrainVisibility({ space: world, terrain: state.terrain, from: footprintCells(world, act), to: [site], edges: state.edges, combatants: state.combatants });
  if (!view.visible) return err(`${action}: ${act.name} has no line of sight to the target cell`);
  return ok(undefined);
}

/**
 * May `actor` activate a granted action against the **unit** `target`? The unit-target sibling of
 * {@link checkActivateSiteRange}. RAW (Core p.65): "To attack or take an action against a target,
 * by default the target must be within RANGE, SENSORS, or THREAT ... and within the attacker's line
 * of sight." Sniper's Mark ("a character within line of sight and range 25") and Crush Targeting
 * ("within line of sight and Sensors") are the two roster actions this wall exists for.
 *
 * Reads the reach off {@link CombatantBase.actionRanges}, exactly as the site wall does, and
 * measures footprint to footprint with {@link combatantRange}, the same distance the attack range
 * wall uses. Line of sight is {@link checkLineOfSight}, the same geometry the attack LoS wall uses.
 * Distance is checked first, so a target that is both too far and hidden reads the distance reason.
 *
 * **Ungated, deliberately:** an action with no frozen reach (the same carve-out as the site wall), a
 * reach that parses `<= 0`, and a target that is the actor itself. An unplaced actor or target is
 * rejected, since neither distance nor sight can be measured.
 */
export type CheckActivateUnitReachArgs = {
  state: EncounterState;
  actor: string;
  action: string;
  target: string;
};

export function checkActivateUnitReach(args: CheckActivateUnitReachArgs): BudgetVerdict {
  const { state, actor, action, target } = args;
  const act = state.combatants[actor];
  if (!act) return err(`activate: ${actor} is not on the map`);
  if (target === actor) return ok(undefined);
  const frozen = act.base.actionRanges?.[action];
  if (!frozen) return ok(undefined);
  const reach = primaryReach(frozen);
  if (reach <= 0) return ok(undefined);
  const tgt = state.combatants[target];
  if (!tgt) return err(`${action}: ${target} is not on the map`);
  const range = combatantRange(state, actor, target);
  if (range === null) {
    return err(`${action}: ${act.name} or ${tgt.name} is not placed on the map (can't measure range)`);
  }
  if (range > reach) {
    return err(`${action}: ${tgt.name} is ${range} away — outside ${act.name}'s reach (Range ${reach})`);
  }
  return checkLineOfSight({ state, viewer: actor, target, verb: action });
}

/**
 * May `attacker` make a **standard weapon attack** against a bare **cell** (the area /
 * cell-origin gate)? The targeted origin cell (an AoE template center, or a
 * single-target shot aimed at ground) must be within the weapon's {@link primaryReach}, measured
 * from the attacker's footprint to the cell via {@link combatantCellRange} — the cell analogue of
 * {@link checkAttackRange}. Gating the **origin** keeps the wall in front of the Blast/Line/Cone
 * fan-out: the template only radiates once its center is legally placed. Reach `<= 0` stands the
 * wall down (the fallback strike), exactly like the unit gate; an unplaced attacker is rejected.
 */
export type CheckAttackCellRangeArgs = {
  state: EncounterState;
  attacker: string;
  cell: Cell;
  weapon?: string;
  profile?: string;
  /** See {@link CheckAttackRangeArgs.rangeBonus}. */
  rangeBonus?: number;
};

export function checkAttackCellRange(args: CheckAttackCellRangeArgs): BudgetVerdict {
  const { state, attacker, cell, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return err(`attack: ${attacker} is not on the map`);
  const selected = selectWeapon(atk, weapon, profile);
  const reach = primaryReach(selected.range ?? []) + (isRangedProfile(selected) ? args.rangeBonus ?? 0 : 0);
  if (reach <= 0) return ok(undefined);
  const range = combatantCellRange(state, attacker, cell);
  if (range === null) {
    return err(`attack: ${attacker} is not placed on the map (can't measure range)`);
  }
  if (range > reach) {
    return err(`attack: target cell is ${range} away — outside ${attacker}'s weapon reach (${reach})`);
  }
  return ok(undefined);
}

/**
 * May `attacker` make a **standard weapon attack** against a bare **cell** given **line of
 * sight to the origin**? RAW (Core p.64): an area attack's template is "drawn from a point
 * within Range **and line of sight**" —
 * the cell-target sibling to {@link checkAttackLoS} (unit target), gating the ORIGIN a
 * Blast/Line/Cone fans out from, exactly like this predicate's job is the cell-target sibling of
 * {@link checkAttackCellRange} (distance). The origin cell is treated as a **Size-1 footprint**,
 * the same convention `combatInspector.ts`'s `armedCellSightlines` uses for its per-cell
 * advisory — reusing the identical `terrainVisibility` geometry directly (`checkLineOfSight`,
 * `search.ts`, only takes combatant ids on both ends, so a bare cell needs this sibling that
 * reads `.visible` straight off `terrainVisibility`; never a third LoS implementation).
 *
 * **Same two RAW exemptions as {@link checkAttackLoS} (checked BEFORE the geometry call):** a
 * weapon carrying `tg_seeking` or `tg_arcing` stands the wall down entirely. **Same reach-unknown
 * carve-out as {@link checkAttackCellRange}:** a weapon with no usable range data (`reach <= 0`,
 * the fallback strike) stands the wall down too — range is *unknown*, not measurable, so LoS
 * can't be meaningfully gated either. An unplaced attacker is an error (LoS can't be measured).
 */
export type CheckAttackCellLoSArgs = {
  state: EncounterState;
  attacker: string;
  cell: Cell;
  weapon?: string;
  profile?: string;
};

export function checkAttackCellLoS(args: CheckAttackCellLoSArgs): BudgetVerdict {
  const { state, attacker, cell, weapon, profile } = args;
  const atk = state.combatants[attacker];
  if (!atk) return err(`attack: ${attacker} is not on the map`);
  const selected = selectWeapon(atk, weapon, profile);
  const isSeeking = selected.tags?.some((t) => t.id === "tg_seeking") ?? false;
  const isArcing = selected.tags?.some((t) => t.id === "tg_arcing") ?? false;
  if (isSeeking || isArcing) return ok(undefined);
  const reach = primaryReach(selected.range ?? []);
  if (reach <= 0) return ok(undefined);
  if (!atk.pos) return err(`attack: ${attacker} is not placed on the map (can't measure line of sight)`);
  const world = worldFor(state);
  const from = footprintCells(world, atk);
  const cut = sightCut(state, atk, [cell]);
  if (cut !== undefined) return err(`attack: ${cut}`);
  const view = terrainVisibility({ space: world, terrain: state.terrain, from, to: [cell], edges: state.edges, combatants: state.combatants });
  if (!view.visible) {
    return err(`attack: ${atk.name} has no line of sight to the target cell`);
  }
  return ok(undefined);
}

/**
 * May `attacker` make a **tech attack** (INVADE and the other tech actions) against `target`? A
 * tech attack is bounded by the attacker's **Sensors range** (`base.sensors`), NOT a weapon's
 * reach — the analogue of the weapon gate for the `tech` branch of the attack intent. Distance is
 * footprint-to-footprint to a unit target ({@link combatantRange}) or footprint-to-cell for a
 * cell target ({@link combatantCellRange}).
 *
 * **No frozen Sensors ⇒ NOT gated** — a combatant that genuinely has no sensors leaves the wall
 * down: range is *unknown*, not zero, mirroring the weapon gate's
 * `reach <= 0` stand-down. When sensors *are* known, an unplaced attacker/target is rejected (the
 * distance can't be measured). **Distance only** — tech "ignores cover" is the resolver's concern.
 */
export type CheckTechRangeArgs = {
  state: EncounterState;
  attacker: string;
  target: AttackTarget;
};

export function checkTechRange(args: CheckTechRangeArgs): BudgetVerdict {
  const { state, attacker, target } = args;
  const atk = state.combatants[attacker];
  if (!atk) return err(`tech: ${attacker} is not on the map`);
  const sensors = atk.base.sensors;
  // No usable Sensors range ⇒ range unknown, not zero — stand the wall down.
  if (sensors === undefined) return ok(undefined);
  const range =
    target.kind === "unit"
      ? combatantRange(state, attacker, target.id)
      : combatantCellRange(state, attacker, target.cell as Cell);
  if (range === null) {
    return err(`tech: ${attacker} or its target is not placed on the map (can't measure Sensors range)`);
  }
  if (range > sensors) {
    const what = target.kind === "unit" ? target.id : "target cell";
    return err(`tech: ${what} is ${range} away — outside ${attacker}'s Sensors range (${sensors})`);
  }
  return ok(undefined);
}

/**
 * The Barrage weapon-allotment legality is a **per-shot** wall — opener and continuation each gate
 * their single weapon off the frozen loadout. Range is the *tactical* tier and stays in the room
 * (per shot). These are structural-tier walls the room runs before dice.
 */

